import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLikedRecipes } from "./useLikedRecipes";
import {
  getPersonalizedRecipes,
  getCustomRecipes,
  getPersonalizedSnapshots,
} from "wasp/client/operations";
import { RecipeDataMapper } from "../services/recipeDataMapper";
import {
  RecipeOrigin,
  StandardRecipe,
  StandardRecipeV1,
} from "../types/StandardRecipe";

export function useMyRecipes() {
  const { likedRecipes = [] } = useLikedRecipes();

  const { data: custom = [], isLoading: loadingCustom } = useQuery({
    queryKey: ["my-recipes", "custom"],
    queryFn: async () => getCustomRecipes({}),
  });

  const { data: personalizedSnapshots, isLoading: loadingPers } = useQuery({
    queryKey: ["my-recipes", "personalized"],
    queryFn: async () => {
      const result = await getPersonalizedSnapshots({ limit: 100 });
      return result;
    },
  });

  const favoritesMapped: StandardRecipeV1[] = useMemo(
    () =>
      likedRecipes.map((r: any) =>
        RecipeDataMapper.toStandardRecipe(r, "favorite"),
      ),
    [likedRecipes],
  );

  const personalizedMapped: StandardRecipeV1[] = useMemo(
    () =>
      (personalizedSnapshots?.recipes ?? []).map((r: any) =>
        RecipeDataMapper.toStandardRecipe(r, "personalized"),
      ),
    [personalizedSnapshots],
  );

  const customMapped: StandardRecipeV1[] = useMemo(
    () =>
      custom.map((r: any) => RecipeDataMapper.toStandardRecipe(r, "custom")),
    [custom],
  );

  const all = useMemo(
    () => [...favoritesMapped, ...customMapped, ...personalizedMapped],
    [favoritesMapped, customMapped, personalizedMapped],
  );

  return {
    all,
    favorites: favoritesMapped,
    custom: customMapped,
    personalized: personalizedMapped,
    isLoading: loadingCustom || loadingPers,
  };
}
